import { extractFieldFromTextBuilder, createRegexSOLITT } from '../genericHelpers';
import { HandlerResult, PasteToQueryBaseHelper } from './pasteToQueryBaseHelper';

const extractField = extractFieldFromTextBuilder();
const extractFieldSOLITT = extractFieldFromTextBuilder(createRegexSOLITT);

interface AbapQueryData {
  system: string;
  objectType: string;
  objectName: string;
  collectionPackage: string;
  collectionOwner: string;
  applicationComponent: string;
  translationArea: string;
  languagesTranslatorTeams: string;
  repositoryProgramId: string;
  repositoryProgramType: string;
  repositoryProgramName: string;
  objectOwner: string;
  maxLength: string;
}

interface SolittDescriptionFieldData extends Pick<AbapQueryData, 'repositoryProgramId'> {
  repositoryObjectType: string;
  repositoryObjectName: string;
}

export class PasteToAbapQueryHelper extends PasteToQueryBaseHelper<AbapQueryData> {
  constructor() {
    super([
      PasteToAbapQueryHelper.getFromSOLITTHandler,
      PasteToAbapQueryHelper.getFromAbapHandler,
      PasteToAbapQueryHelper.getFromSE63Handler
    ]);
  }

  private static getFromSOLITTHandler(
    text: string,
    { data }: HandlerResult<AbapQueryData>
  ): HandlerResult<AbapQueryData> {
    data.system = extractFieldSOLITT('System', text);
    data.objectType = extractFieldSOLITT('Object Type', text);
    data.objectName = extractFieldSOLITT('Object Name', text);
    data.collectionPackage = extractFieldSOLITT('Collection Name', text);
    data.objectOwner = extractFieldSOLITT('Contact Person', text);
    data.applicationComponent = extractFieldSOLITT('Application Component', text);
    data.translationArea = extractFieldSOLITT('Translation Area', text);
    data.languagesTranslatorTeams = extractFieldSOLITT('Target Languages-Translator Teams', text);

    const description = extractFieldSOLITT('Description', text);
    if (description.trim()) {
      const parseablePart = description.substring(description.lastIndexOf(' ') + 1);
      const regExp = new RegExp(
        '(?<repositoryProgramId>.{4})(?<repositoryObjectType>.{4})(?<repositoryObjectName>.*)'
      );
      const repositoryData = {
        ...regExp.exec(parseablePart)?.groups
      } as unknown as SolittDescriptionFieldData;

      data.repositoryProgramId = repositoryData.repositoryProgramId;
      data.repositoryProgramName = repositoryData.repositoryObjectName;
      data.repositoryProgramType = repositoryData.repositoryObjectType;

      const done =
        !!data.repositoryProgramId && !!data.repositoryProgramName && !!data.repositoryProgramType;

      return {
        data,
        done
      };
    }

    return { data, done: false };
  }

  private static getFromAbapHandler(
    text: string,
    { data }: HandlerResult<AbapQueryData>
  ): HandlerResult<AbapQueryData> {
    data.system = extractField('System', text);
    data.objectType = extractField('Object Type', text);
    data.objectName = extractField('Object Name', text);
    data.collectionPackage = extractField('Collection', text);
    data.collectionOwner = extractField('Collection Owner', text);
    data.applicationComponent = extractField('Application Component', text);
    data.repositoryProgramId = extractField('Repository Program ID', text);
    data.translationArea = extractField('Translation Area', text);
    data.languagesTranslatorTeams = extractField('Target Languages-Translator Teams', text);
    data.repositoryProgramType = extractField('Repository Program Type', text);
    data.repositoryProgramName = extractField('Repository Program Name', text);
    data.objectOwner = extractField('Object Owner', text);
    data.maxLength = extractField('Maximum Permitted Length of Translation', text);

    const done = !!data.repositoryProgramType && !!data.repositoryProgramName;

    return { data, done };
  }

  private static getFromSE63Handler(
    text: string,
    { data }: HandlerResult<AbapQueryData>
  ): HandlerResult<AbapQueryData> {
    if (data.repositoryProgramType === '') {
      data.repositoryProgramType = extractField('Repository Object Type', text);
    }
    if (data.repositoryProgramName === '') {
      data.repositoryProgramName = extractField('Repository Object Name', text);
    }
    if (data.objectOwner === '') {
      data.objectOwner = extractField('Contact Person', text);
    }

    return { data };
  }
}
